import { Controller } from "@hotwired/stimulus";

// Controlador principal del AutoDmForm — consolida la lógica de toggles que en
// el frontend React vivían en `useState` del componente AutoDmForm.tsx:
//   - hook_kind: comment / dm / story → muestra/oculta replyStep + postsBlock + storiesBlock,
//                                       fuerza send_dm=true en dm/story.
//   - send_dm: yes / no → muestra/oculta messageBlock (textarea + allow_btn + url section).
//   - send_reply: yes / no → muestra/oculta replyBlock (textarea reply).
//
// El toggle de allow_btn (mostrar/ocultar url + label_button) lo maneja el
// controller estándar `manage--toggle-field`. El selector de state usa el
// partial compartido `manage/shared/_switch_state` (radios nativos + CSS via
// `has-[:checked]`, sin JS).
//
// También escucha el evento custom `autodm:posts-confirmed` (despachado por el modal
// `posts_modal_controller.js`) para actualizar los hidden inputs de post_ids[] y
// el label del botón "Activo en N publicaciones".

export default class extends Controller {
  static targets = [
    // Step 1
    "postsBlock", "postsLabel", "postsLink", "storiesBlock", "postIdsContainer",
    // Step 2
    "messageBlock",
    // Step 3
    "replyStep", "replyBlock"
  ];

  static values = {
    hookKind: { type: String, default: "comment" },
    sendDm: { type: String, default: "true" },
    sendReply: { type: String, default: "false" }
  };

  connect() {
    this._onPostsConfirmed = this._onPostsConfirmed.bind(this);
    document.addEventListener("autodm:posts-confirmed", this._onPostsConfirmed);
    this._applyHookKind();
    this._applySendDm();
    this._applySendReply();
  }

  disconnect() {
    document.removeEventListener("autodm:posts-confirmed", this._onPostsConfirmed);
  }

  // -------- Step 1: hook_kind --------
  changeHookKind(event) {
    const value = event.target.value;
    this.hookKindValue = value;

    // Cuando hook = dm o story, fuerza send_dm = true (replica frontend lines 195-198).
    if (value === "dm" || value === "story") {
      this.sendDmValue = "true";
      const sendDmSelect = this.element.querySelector("select[name='autodm_form[send_dm]']");
      if (sendDmSelect) {
        sendDmSelect.value = "true";
        sendDmSelect.disabled = true;
      }
      this._applySendDm();
    } else {
      const sendDmSelect = this.element.querySelector("select[name='autodm_form[send_dm]']");
      if (sendDmSelect) sendDmSelect.disabled = false;
    }

    this._applyHookKind();
  }

  _applyHookKind() {
    const v = this.hookKindValue;
    this._toggle(this.postsBlockTarget,   v === "comment");
    this._toggle(this.storiesBlockTarget, v === "story");
    this._toggle(this.replyStepTarget,    v === "comment");
  }

  // -------- Step 2: send_dm --------
  changeSendDm(event) {
    this.sendDmValue = event.target.value;
    this._applySendDm();
  }

  _applySendDm() {
    this._toggle(this.messageBlockTarget, this.sendDmValue === "true");
  }

  // -------- Step 3: send_reply --------
  changeSendReply(event) {
    this.sendReplyValue = event.target.value;
    this._applySendReply();
  }

  _applySendReply() {
    this._toggle(this.replyBlockTarget, this.sendReplyValue === "true");
  }

  // -------- Posts modal callback (custom event from posts_modal_controller) --------
  _onPostsConfirmed(event) {
    const ids = (event.detail && event.detail.ids) || [];
    if (!this.hasPostIdsContainerTarget) return;

    // 1. Hidden inputs del form (lo que se submitea al server).
    //    Solo el ID raw — el form_object asume `type: "post"` y normaliza a
    //    `{"id" => id, "type" => "post"}` antes de persistir.
    this.postIdsContainerTarget.innerHTML = "";
    ids.forEach((id) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "autodm_form[post_ids][]";
      input.value = String(id);
      this.postIdsContainerTarget.appendChild(input);
    });

    // 2. Label visible del botón "Habilitado para todas..." / "Activo en N publicaciones".
    if (this.hasPostsLabelTarget) {
      const labels = this._labels();
      if (ids.length === 0) {
        this.postsLabelTarget.textContent = labels.allPosts;
      } else if (ids.length === 1) {
        this.postsLabelTarget.textContent = labels.activeInPost.replace("%{count}", ids.length);
      } else {
        this.postsLabelTarget.textContent = labels.activeInPosts.replace("0", ids.length).replace("%{count}", ids.length);
      }
    }

    // 3. href del link al modal: si se reabre, los IDs actuales deben aparecer
    //    como current. Reconstruyo `?selected_ids[]=X&selected_ids[]=Y...`
    //    sobre la `data-base-url` del propio link (path sin query).
    if (this.hasPostsLinkTarget) {
      const baseUrl = this.postsLinkTarget.dataset.baseUrl;
      if (baseUrl) {
        const qs = ids.map((id) => `selected_ids%5B%5D=${encodeURIComponent(id)}`).join("&");
        this.postsLinkTarget.setAttribute("href", qs ? `${baseUrl}?${qs}` : baseUrl);
      }
    }
  }

  _labels() {
    if (this._labelsCache) return this._labelsCache;
    const ds = this.element.dataset;
    this._labelsCache = {
      allPosts:       ds.labelAllPosts       || "Habilitado para todas tus publicaciones",
      activeInPost:   ds.labelActiveInPost   || "Activo en %{count} publicación",
      activeInPosts:  ds.labelActiveInPosts  || "Activo en %{count} publicaciones"
    };
    return this._labelsCache;
  }

  _toggle(el, show) {
    if (!el) return;
    el.style.display = show ? "" : "none";
  }
}
